import * as css from './BrandingPage.css';
import {createContext, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PageWrapper from '../components/pageWrapper/PageWrapper';
import {getEmailData} from '../../redux/branding/brandingActions';
import BrandingMenu from './menu/menu';
import {useSelector} from 'react-redux';
import {useParams, useNavigate} from 'react-router-dom';
import {getEventSettings} from '../../redux/event/eventActions';
import {hasAccountAccess} from '../../functions/permission';
import BtnLanguages from '../../component/button/btnLanguages/BtnLanguages';
import useMobile from '../../hooks/useMobile';
import {getPackageProducer} from '../../redux/account/accountActions';
import {setCurrentRating} from '../components/rating/Rating';
import setGlobalState from '../../redux/setGlobalState';
import TickchakLoader from '../../component/tickchakLoader/TickchakLoader';
import {messageTypeConfig, createScreensTypes} from './menu/brandingConfig';
import Btn from '../../component/button/btn/Btn';
import useGlobalShow from '../../hooks/useGlobalShow';
import {eventPathHelper} from './menu/brandingHelper';
import AIGeneratorButton from '../../component/aiMessageGenerator/aiGeneratorButton/AIGeneratorButton';
import useShow from '../../hooks/useShow';
import {IS_NEW_TEMPLATE} from '../../config/config';
import useTablet from '../../hooks/useTablet';
import {logGoogleEvent} from '../../utils/analytics';

export const BrandingContext = createContext();

function BrandingPage() {
    const {t}=useTranslation('branding');
    const {t: tWhatsApp}=useTranslation('whatsapp');
    const account = useSelector(s=>s.account);
    const event = useSelector(s=>s.event);
    const {currentLang, getBrandingData,updateDesignEmailXHR}=useSelector(s=>s.branding);
    const params = useParams();
    const navigate = useNavigate();
    const messageType = params.messageType || 'sendTicket';
    const deliveryType = params.deliveryType || 'email';

    const [currentEditorName,setCurrentEditorName]=useState();
    const [message, setMessage] = useState(null);

    const eventWhatsAppStatus = hasAccountAccess('whatsApp') && event.settings?.ticketsMailing?.whatsAppMessage;
    // Check if current delivery type is allowed for the message type
    const currentMessageConfig = messageTypeConfig[messageType];
    const allowedDeliveryTypes = currentMessageConfig?.allowedDeliveryTypes || ['email'];
    const isCurrentDeliveryTypeAllowed = allowedDeliveryTypes.includes(deliveryType);

    // Redirect if current delivery type is not allowed
    useEffect(() => {
        if(!isCurrentDeliveryTypeAllowed) {
            const targetDeliveryType = allowedDeliveryTypes[0];
            const eventPath = eventPathHelper[event.type] || eventPathHelper.default;
            navigate(`/e/${event.eid}/${eventPath}/branding/${messageType}/${targetDeliveryType}`, {replace: true});
        }
    }, [messageType, deliveryType, event.eid, event.type]);

    const screensTypes = useMemo(()=> createScreensTypes(eventWhatsAppStatus, account.packagesProducer),[eventWhatsAppStatus, account.packagesProducer, deliveryType, messageType]);

    const {Comps,CompPreview,CompPreviewProps}=screensTypes[deliveryType]||screensTypes['email'];
    const Comp = Comps[messageType];

    // Get the correct preview component based on delivery type
    const PreviewComponent = deliveryType === 'email'
        ? CompPreview[messageType]
        : CompPreview;

    // Prepare props for preview component
    const previewProps = {...CompPreviewProps};
    if(deliveryType === 'whatsapp' && CompPreviewProps?.whatsappTemplate) {
        previewProps.whatsappTemplate = CompPreviewProps.whatsappTemplate(messageType, event.type, event.eid);
    }

    useEffect(()=>{
        if(!event.getEventSettingsXHR)
            getEventSettings().then();
        if(!currentLang && event.lang){
            setGlobalState('branding.currentLang', event.lang);
        }
        return ()=>{
            if(updateDesignEmailXHR==='SUCCESS')
                setCurrentRating('brandingTicket');
        };
    },[]);

    useEffect(()=>{
        if(!getBrandingData?.[`${currentLang}XHR`])
            getEmailData().then();
    },[currentLang]);

    useEffect( () => {
        if(account.pid&& ['SUCCESS','UPDATED'].includes(account.getAccountXHR))
            getPackageProducer().then();
    }, [account.getAccountXHR]);

    const isMobile = useMobile();
    const isTablet = useTablet();
    const trialPopupShow = useGlobalShow('isOpenTrialPopup');
    const trialPopShow = useGlobalShow('isOpenTrialPop');
    const aiGeneratorShow = useShow();
    const newService = IS_NEW_TEMPLATE(Number(event.eid));

    const openAiGenerator = useCallback(() => {
        if(event.eid) {
            logGoogleEvent('ai_message_generator_open', event.eid, {
                popup_name: 'aiMessageGenerator',
                message_type: messageType,
                feature: 'whatsapp',
            });
        }
        aiGeneratorShow.open();
    }, [event.eid, messageType, aiGeneratorShow]);

    const WhatsAppTrialButton = () => (
        <Btn onClick={() => {trialPopupShow.open(); trialPopShow.open();}} dataCy="whatsapp-send-trial-btn">
            {tWhatsApp('sendTrial')}
        </Btn>
    );
    
    const AIGeneratorButtonProps = {
        label: tWhatsApp('aiGenerator.triggerLabel'),
        onClick: openAiGenerator,
        'data-testid': 'whatsapp-ai-generator-trigger',
    };
    const WhatsAppButtons = [
        ...(newService ? [{key: 1, Comp: AIGeneratorButton, props: {...AIGeneratorButtonProps, cssStyle: 'margin-inline: var(--space-lg);',
        }}] : []),
        {key: 2, Comp: WhatsAppTrialButton},
    ];

    const operations = (() => {
        const buttons = [];
        if(!isMobile) {
            buttons.push({key: 0, Comp: BtnLanguages});
        }
        if(deliveryType === 'whatsapp' && eventWhatsAppStatus) {
            buttons.push(...WhatsAppButtons);
        }        
        return {buttons};
    })();

    const mobileActions = (() => {
        const actions = [];
        if(deliveryType === 'whatsapp') {
            actions.push({
                text: tWhatsApp('sendTrial'),
                onClick: () => {
                    trialPopupShow.open();
                    trialPopShow.open();
                }
            });
        }
        return actions;
    })();
    if(event.getEventSettingsXHR==='LOADING') {
        return <TickchakLoader showClass={'show'}/>;
    }
    return (
        <>
            <BrandingContext.Provider value={{
                textEditor: {currentEditorName, setCurrentEditorName},
                ...(deliveryType === 'whatsapp' && {whatsapp: {message, setMessage, aiGeneratorShow}})
            }}>
                <PageWrapper image={'ticketsGroups'}
                    routing={{subTitle:t(deliveryType),title:t('branding'),id:'branding'}}
                    SubMenuChildren={PreviewComponent || null}
                    subMenuProps={CompPreviewProps}
                    operations={operations}
                    leftTitle={t('preview')}
                    mobileActions={mobileActions}
                >
                    <>
                        <css.container data-cy='brandingPage'>
                            {isTablet&&
                            <css.languageBtn>
                                <BtnLanguages/>
                                {deliveryType === 'whatsapp' && <AIGeneratorButton {...AIGeneratorButtonProps} />}
                            </css.languageBtn>}
                            <css.designEmailArea>
                                <BrandingMenu />
                                <css.left>
                                    {Comp && <Comp />}
                                </css.left>
                            </css.designEmailArea>
                        </css.container>
                    </>
                    {PreviewComponent && <PreviewComponent {...previewProps}/>}
                </PageWrapper>
            </BrandingContext.Provider>
        </>

    );
}

export default BrandingPage;
